/** external */
import _ from "lodash";
import React from "react";
import update from "immutability-helper";
import {
  HamburgerOutlined,
  GlobeOutlined,
  TickCircleOpenOutlined,
  ClockOutlined,
  PageFoldPortraitOutlined,
  ArchiveOutlined,
  ClipboardTickOutlined,
  ClipboardPencilLinesOutlined,
} from "@toddle-edu/ds-icons";

/** internal */
import { getSelectedCurriculumId } from "modules/CommonSelectors";
import { getQuestionAssociatedEntityFromCache } from "QuestionBank/modules/QuestionBankGraphqlHelper";
import { getTodaysDate } from "Utils/DateTimeHelpers";
import { moveElementInArray, extractContentFromHTML } from "Utils";
import QuestionCardTag from "Worksheet/components/QuestionCardTag";
import {
  getAccessLevels,
  MODULES,
  RULE_OPTIONS,
  getIsAdminByRole,
} from "lib/staffPermissionUtils";
import {
  FIELD_UID_SCORE,
  QUESTION_TYPES,
} from "Worksheet/modules/WorksheetConstants";
import {
  getFilteredQuestionTypeOptions,
  getDummySubmissionsFromQuestions,
  getLibraryQuestionCardDetailsV2,
  checkForImageInHtmlString,
} from "Worksheet/modules/WorksheetUtils";
import { getFormattedStaffName } from "Utils/GetFormattedStaffName";

/** relative */
import {
  GRADE,
  SUBJECT,
  QUESTION_PAPER,
  PLANNER_ELEMENT_NODE,
  DRAFT,
  APPROVED,
  PUBLISHED,
  PENDING_REVIEW,
  ARCHIVED,
  ALL_QUESTION,
  questionFeedFilterModalKeys,
  TOPIC_DEPTH,
  SUB_TOPIC_DEPTH,
  CONTENT_DEPTH,
  CORRECTION_COMPLETED,
  CORRECTION_REQUIRED,
  MYP_LEARNING_OBJECTIVES,
  MYP_KEY_CONCEPT,
  MYP_GLOBAL_CONTEXT,
  MYP_RELATED_CONCEPT,
  MYP_LIBRARY_TOPICS,
  SUBJECT_LEVEL,
  QUESTION_STATUS_COLORS,
} from "./Constants";

export const getDifficultyLevelOptions = t => {
  return [
    {
      label: t("worksheet:easy"),
      value: "EASY",
      key: "EASY",
      localeLabel: t("worksheet:easy"),
    },
    {
      label: t("worksheet:medium"),
      value: "MEDIUM",
      key: "MEDIUM",
      localeLabel: t("worksheet:medium"),
    },
    {
      label: t("worksheet:hard"),
      value: "HARD",
      key: "HARD",
      localeLabel: t("worksheet:hard"),
    },
  ];
};

export const getStatusOptions = t => [
  {
    label: t("common:draft"),
    value: DRAFT,
  },
  {
    label: t("worksheet:pending_review"),
    value: PENDING_REVIEW,
  },
  {
    label: t("worksheet:requires_correction"),
    value: CORRECTION_REQUIRED,
  },
  {
    label: t("worksheet:correction_completed"),
    value: CORRECTION_COMPLETED,
  },
  {
    label: t("common:approved"),
    value: APPROVED,
  },
  {
    label: t("common:published"),
    value: PUBLISHED,
  },
];

/**
 *
 * @deprecated - use questionDetailsV2Fragment instead, which is updated for V2 question details cache structure
 * @param {Object} questionData - data to the question fetched from the cache
 * @param {Object} activeQuestionFilters - current filters that are applied to the question
 * @returns - returns the associated entities that are added and removed to be sent to the update mutation
 */
export const getAssociatedEntityVariables = (
  questionData,
  activeQuestionFilters
) => {
  const addedAssociatedEntities = [],
    removedAssociatedEntities = [];

  const {
    grades,
    subject,
    paperTypes,
    criterion,
    keyConcepts,
    globalContext,
    relatedConcepts,
    plannerElements,
    objectives,
    subjectLevels,
  } = activeQuestionFilters;

  const oldAssociatedEntities = _.get(
    questionData,
    "item.associatedEntities",
    []
  );

  /**
   * if the tag is there in the filters and not there in the existing question data, add it to addedAssociatedEntities
   * if the tag not there in the filters and there in the existing question data, add it to removedAssociatedEntities
   */

  /** 1. Grades */
  const oldGrades = _.map(
    _.filter(oldAssociatedEntities, entity => {
      return entity.__typename == "Grade";
    }),
    oldGrade => {
      return {
        id: oldGrade?.id,
        type: GRADE,
      };
    }
  );
  if (!_.isEmpty(grades) || !_.isEmpty(oldGrades)) {
    const newGrades = _.map(grades, grade => ({
      id: grade?.value,
      type: GRADE,
    }));

    addedAssociatedEntities.push(..._.differenceBy(newGrades, oldGrades, "id"));
    removedAssociatedEntities.push(
      ..._.differenceBy(oldGrades, newGrades, "id")
    );
  }

  /** 2. Subjects */
  const oldSubjects = !_.isEmpty(oldAssociatedEntities)
    ? _.map(
        _.filter(
          oldAssociatedEntities,
          entity => entity.__typename === "Subject"
        ),
        oldSubject => ({
          id: oldSubject?.id,
          type: SUBJECT,
        })
      )
    : [];
  if (!_.isEmpty(subject) || !_.isEmpty(oldSubjects)) {
    const newSubjects = _.map(subject, sub => ({
      id: sub,
      type: SUBJECT,
    }));
    addedAssociatedEntities.push(
      ..._.differenceBy(newSubjects, oldSubjects, "id")
    );
    removedAssociatedEntities.push(
      ..._.differenceBy(oldSubjects, newSubjects, "id")
    );
  }

  /**Paper Types */
  const oldPaperTypes = _.map(
    _.filter(oldAssociatedEntities, entity => {
      return _.get(entity, "tagType", "") == QUESTION_PAPER;
    }),
    oldPaperType => {
      return {
        id: oldPaperType.id,
        type: QUESTION_PAPER,
      };
    }
  );
  if (!_.isEmpty(paperTypes) || !_.isEmpty(oldPaperTypes)) {
    const newPaperTypes = _.map(paperTypes, paperType => ({
      id: paperType.value,
      type: QUESTION_PAPER,
    }));

    addedAssociatedEntities.push(
      ..._.differenceBy(newPaperTypes, oldPaperTypes, "id")
    );
    removedAssociatedEntities.push(
      ..._.differenceBy(oldPaperTypes, newPaperTypes, "id")
    );
  }

  /** Subject Levels */
  const oldSubjectLevels = _.map(
    _.filter(oldAssociatedEntities, entity => {
      return _.get(entity, "tagType", "") == SUBJECT_LEVEL;
    }),
    oldSubjectLevel => {
      return {
        id: oldSubjectLevel.id,
        type: SUBJECT_LEVEL,
      };
    }
  );

  if (!_.isEmpty(subjectLevels) || !_.isEmpty(oldSubjectLevels)) {
    const newSubjectLevels = _.map(subjectLevels, subjectLevel => ({
      id: subjectLevel.value,
      type: SUBJECT_LEVEL,
    }));

    addedAssociatedEntities.push(
      ..._.differenceBy(newSubjectLevels, oldSubjectLevels, "id")
    );
    removedAssociatedEntities.push(
      ..._.differenceBy(oldSubjectLevels, newSubjectLevels, "id")
    );
  }

  /** Planner Elements */
  const oldPlannerElements = !_.isEmpty(oldAssociatedEntities)
    ? _.map(
        _.filter(oldAssociatedEntities, ["__typename", "PlannerElementNode"]),
        oldPlannerElement => {
          return {
            id: oldPlannerElement?.id,
            type: PLANNER_ELEMENT_NODE,
          };
        }
      )
    : [];

  const allPlannerElements = _.compact(
    _.concat(
      plannerElements,
      _.map(objectives, "value"),
      _.map(criterion, "value"),
      _.map(keyConcepts, "value"),
      _.map(globalContext, "value"),
      _.map(relatedConcepts, "value")
    )
  );

  if (!_.isEmpty(allPlannerElements) || !_.isEmpty(oldPlannerElements)) {
    const newPlannerElements = _.map(allPlannerElements, plannerElement => ({
      id: plannerElement,
      type: PLANNER_ELEMENT_NODE,
    }));

    addedAssociatedEntities.push(
      ..._.differenceBy(newPlannerElements, oldPlannerElements, "id")
    );
    removedAssociatedEntities.push(
      ..._.differenceBy(oldPlannerElements, newPlannerElements, "id")
    );
  }

  return {
    addedAssociatedEntities: _.compact(addedAssociatedEntities),
    removedAssociatedEntities: _.compact(removedAssociatedEntities),
  };
};

/**
 * @description - converts the associated entities in question to the filters format stored in the redux state
 * @returns - returns the active filters of the question to be set in redux state
 * @param {Object} questionDetails - question object fetched from the cache
 */
export const getActiveQuestionFilters = questionDetails => {
  const associatedEntities = _.get(questionDetails, "associatedEntities", []);

  const grades = [],
    paperTypes = [],
    subject = [],
    criterion = [],
    keyConcepts = [],
    globalContext = [],
    relatedConcepts = [],
    plannerElements = [],
    objectives = [],
    subjectLevels = [];

  associatedEntities.forEach(entity => {
    switch (entity?.__typename) {
      case "Grade":
        grades.push({ value: entity.id, label: entity.name });
        break;
      case "Subject":
        {
          subject.push(entity.id);
        }
        break;
      case "GenericTag": {
        switch (entity?.tagType) {
          case QUESTION_PAPER:
            paperTypes.push({ value: entity.id, label: entity.label });
            break;
          case SUBJECT_LEVEL:
            subjectLevels.push({ value: entity.id, label: entity.label });
            break;
          default:
            break;
        }
        break;
      }
      case "PlannerElementNode": {
        switch (entity?.elementType) {
          case MYP_LEARNING_OBJECTIVES:
            criterion.push({ value: entity.id, label: entity.label });
            break;
          case MYP_KEY_CONCEPT:
            keyConcepts.push({ value: entity.id, label: entity.label });
            break;
          case MYP_GLOBAL_CONTEXT:
            globalContext.push({ value: entity.id, label: entity.label });
            break;
          case MYP_RELATED_CONCEPT:
            _.forEach(entity?.associatedParents, parent => {
              if (parent?.__typename === "Subject") {
                relatedConcepts.push({
                  value: entity?.id,
                  label: entity?.label,
                  subjectId: parent?.id,
                });
              }
            });
            break;
          case MYP_LIBRARY_TOPICS:
            _.forEach(entity?.associatedParents, parent => {
              if (parent?.__typename === "Subject") {
                objectives.push({
                  value: entity?.id,
                  subjectId: parent?.id,
                });
              }
            });
            break;
          default:
            plannerElements.push(entity.id);
            break;
        }
        break;
      }
      default:
        break;
    }
  });

  return {
    difficulty: _.get(questionDetails, "difficulty", null),
    subject,
    grades,
    paperTypes,
    criterion,
    keyConcepts,
    globalContext,
    relatedConcepts,
    plannerElements,
    objectives,
    subjectLevels,
  };
};

export const getMultiPartQuestionScore = _.memoize(
  data => {
    return _.sumBy(_.get(data, "childrenV2.edges", []), edge => {
      const scoreField = _.find(_.get(edge, "node.item.fields", []), {
        uid: FIELD_UID_SCORE,
      });
      return _.toNumber(_.get(scoreField, "value", 0) || 0);
    });
  },
  data => {
    const edges = _.get(data, "childrenV2.edges", []);
    const values = _.map(edges, edge => {
      const fields = _.get(edge, "node.item.fields", []);
      const scoreField = _.find(fields, { uid: FIELD_UID_SCORE });
      return _.get(scoreField, "value", 0);
    });
    return _.join(values, "|");
  }
);

export const isFeedFiltersEmpty = filters => {
  const filtersWithoutArchivedAndStatus = _.omit(filters, [
    "isArchived",
    "status",
  ]);
  return _.every(
    _.values(filtersWithoutArchivedAndStatus),
    value => _.isEmpty(value) || _.isNil(value)
  );
};

export const getQuestionLibraryCreationAccess = () => {
  return getAccessLevels({
    [MODULES.QUESTION_LIBRARY.id]: {
      hasAdminAccess: {
        rule: RULE_OPTIONS.eq,
        value: MODULES.QUESTION_LIBRARY.values.ADMIN_ACCESS.id,
        defaultAccess: getIsAdminByRole() ? true : false,
      },
      hasCreatorAccess: {
        rule: RULE_OPTIONS.gte,
        value: MODULES.QUESTION_LIBRARY.values.CREATOR_ACCESS.id,
        defaultAccess: true,
      },
    },
  });
};

export const getQuestionBankStatusOptions = ({
  t,
  questionStatusWiseCount = {},
  totalQuestionCount,
}) => {
  const allOptions = {
    PENDING_REVIEW: {
      label: t("worksheet:add_for_review"),
      key: PENDING_REVIEW,
    },
    APPROVED: {
      label: t("worksheet:move_to_approved"),
      key: APPROVED,
    },
    DRAFT: { label: t("worksheet:move_to_draft_plural"), key: DRAFT },
    CORRECTION_REQUIRED: {
      label: t("worksheet:requires_correction"),
      key: CORRECTION_REQUIRED,
    },
    CORRECTION_COMPLETED: {
      label: t("worksheet:correction_completed"),
      key: CORRECTION_COMPLETED,
    },
    PUBLISHED: { label: t("common:publish"), key: PUBLISHED },
  };
  const counts = _.mapValues(
    _.keyBy(questionStatusWiseCount, "status"),
    "count"
  );

  return [
    {
      key: ALL_QUESTION,
      icon: <HamburgerOutlined variant="subtle" size="x-small" />,
      label: t("common:all_label", { label: t("common:question_plural") }),
      count: totalQuestionCount,
      value: { isArchived: false },
    },
    {
      key: PUBLISHED,
      icon: <GlobeOutlined variant="subtle" size="x-small" />,
      label: t("common:published"),
      value: { status: PUBLISHED, isArchived: false },
      count: counts[PUBLISHED],
      adminFooterConfig: {
        primaryButton: {
          label: t("common:next"),
        },
        secondaryButton: {
          label: t("worksheet:add_to_draft_plural"),
          value: DRAFT,
        },
        dropdownOptions: [
          allOptions.PENDING_REVIEW,
          allOptions.APPROVED,
          allOptions.CORRECTION_REQUIRED,
          allOptions.CORRECTION_COMPLETED,
        ],
        isPrimaryButtonNext: true,
      },
      creatorFooterConfig: {
        primaryButton: {
          label: t("common:next"),
        },
        secondaryButton: {
          label: t("worksheet:add_to_draft_plural"),
          value: DRAFT,
        },
        dropdownOptions: [
          allOptions.PENDING_REVIEW,
          allOptions.CORRECTION_REQUIRED,
          allOptions.CORRECTION_COMPLETED,
        ],
        isPrimaryButtonNext: true,
      },
    },
    {
      key: APPROVED,
      icon: <TickCircleOpenOutlined variant="subtle" size="x-small" />,
      label: t("common:approved"),
      value: { status: APPROVED, isArchived: false },
      count: counts[APPROVED],
      adminFooterConfig: {
        primaryButton: {
          icon: <GlobeOutlined />,
          label: t("common:publish"),
          value: PUBLISHED,
        },
        secondaryButton: {
          label: t("common:next"),
        },
        dropdownOptions: [
          allOptions.PENDING_REVIEW,
          allOptions.DRAFT,
          allOptions.CORRECTION_REQUIRED,
          allOptions.CORRECTION_COMPLETED,
        ],
        isPrimaryButtonNext: false,
      },
      creatorFooterConfig: {
        primaryButton: {
          label: t("common:next"),
        },
        secondaryButton: {
          label: t("worksheet:add_for_review"),
          value: PENDING_REVIEW,
        },
        dropdownOptions: [
          allOptions.DRAFT,
          allOptions.CORRECTION_REQUIRED,
          allOptions.CORRECTION_COMPLETED,
        ],
        isPrimaryButtonNext: true,
      },
    },
    {
      key: CORRECTION_COMPLETED,
      icon: <ClipboardTickOutlined variant="subtle" size="x-small" />,
      label: t("worksheet:correction_completed"),
      value: { status: CORRECTION_COMPLETED, isArchived: false },
      count: counts[CORRECTION_COMPLETED],
      adminFooterConfig: {
        primaryButton: {
          icon: <TickCircleOpenOutlined />,
          label: t("common:approve"),
          value: APPROVED,
        },
        secondaryButton: {
          label: t("common:next"),
        },
        dropdownOptions: [
          allOptions.PENDING_REVIEW,
          allOptions.DRAFT,
          allOptions.CORRECTION_REQUIRED,
        ],
        isPrimaryButtonNext: false,
      },
      creatorFooterConfig: {
        primaryButton: {
          label: t("common:next"),
        },
        secondaryButton: {
          label: t("worksheet:add_for_review"),
          value: PENDING_REVIEW,
        },

        dropdownOptions: [
          allOptions.PENDING_REVIEW,
          allOptions.CORRECTION_REQUIRED,
          allOptions.DRAFT,
        ],
        isPrimaryButtonNext: true,
      },
    },
    {
      key: CORRECTION_REQUIRED,
      icon: <ClipboardPencilLinesOutlined variant="subtle" size="x-small" />,
      label: t("worksheet:requires_correction"),
      value: { status: CORRECTION_REQUIRED, isArchived: false },
      count: counts[CORRECTION_REQUIRED],
      adminFooterConfig: {
        primaryButton: {
          label: t("worksheet:correction_completed"),
          value: CORRECTION_COMPLETED,
        },
        secondaryButton: {
          label: t("common:next"),
        },
        dropdownOptions: [
          allOptions.PENDING_REVIEW,
          allOptions.APPROVED,
          allOptions.DRAFT,
        ],
        isPrimaryButtonNext: false,
      },
      creatorFooterConfig: {
        primaryButton: {
          label: t("worksheet:correction_completed"),
          value: CORRECTION_COMPLETED,
        },
        secondaryButton: {
          label: t("common:next"),
        },
        dropdownOptions: [allOptions.PENDING_REVIEW, allOptions.DRAFT],
        isPrimaryButtonNext: false,
      },
    },
    {
      key: PENDING_REVIEW,
      icon: <ClockOutlined variant="subtle" size="x-small" />,
      label: t("worksheet:pending_review"),
      value: { status: PENDING_REVIEW, isArchived: false },
      count: counts[PENDING_REVIEW],
      adminFooterConfig: {
        primaryButton: {
          icon: <TickCircleOpenOutlined />,
          label: t("common:approve"),
          value: APPROVED,
        },
        secondaryButton: {
          label: t("common:next"),
        },
        dropdownOptions: [
          allOptions.DRAFT,
          allOptions.CORRECTION_REQUIRED,
          allOptions.CORRECTION_COMPLETED,
        ],
        isPrimaryButtonNext: false,
      },
      creatorFooterConfig: {
        primaryButton: {
          label: t("common:next"),
        },
        secondaryButton: {
          icon: <PageFoldPortraitOutlined />,
          label: t("worksheet:move_to_draft_plural"),
          value: DRAFT,
        },
        dropdownOptions: [
          allOptions.CORRECTION_REQUIRED,
          allOptions.CORRECTION_COMPLETED,
        ],
        isPrimaryButtonNext: true,
      },
    },
    {
      key: DRAFT,
      icon: <PageFoldPortraitOutlined variant="subtle" size="x-small" />,
      label: t("common:draft"),
      value: { status: DRAFT, isArchived: false },
      count: counts[DRAFT],
      adminFooterConfig: {
        primaryButton: {
          label: t("worksheet:add_for_review"),
          value: PENDING_REVIEW,
        },
        secondaryButton: {
          label: t("common:next"),
        },
        dropdownOptions: [
          allOptions.APPROVED,
          allOptions.CORRECTION_REQUIRED,
          allOptions.CORRECTION_COMPLETED,
        ],
        isPrimaryButtonNext: false,
      },
      creatorFooterConfig: {
        primaryButton: {
          label: t("worksheet:add_for_review"),
          value: PENDING_REVIEW,
        },
        secondaryButton: {
          label: t("common:next"),
        },
        dropdownOptions: [
          allOptions.CORRECTION_REQUIRED,
          allOptions.CORRECTION_COMPLETED,
        ],
        isPrimaryButtonNext: false,
      },
    },
    {
      key: "divider",
      isDivider: true,
    },
    {
      key: ARCHIVED,
      icon: <ArchiveOutlined variant="subtle" size="x-small" />,
      label: t("common:archived"),
      value: { isArchived: true },
    },
  ];
};

export const getQuestionBankStatusOptionsMemoized = _.memoize(
  params => getQuestionBankStatusOptions(params),
  params => {
    const statusCounts = _.get(params, "questionStatusWiseCount", []);
    const sortedCounts = _.sortBy(statusCounts, "status");
    const questionStatusWiseCount = _.map(sortedCounts, item =>
      _.pick(item, ["status", "count"])
    );
    return JSON.stringify({
      questionStatusWiseCount,
      totalQuestionCount: _.get(params, "totalQuestionCount"),
    });
  }
);
/**
 * @description - extracts question template from the question node query and
 * @returns - returns the question template and question details
 * @param {Object} data
 */

/**
 * @param {Object} state - redux state
 * @returns the variables for the question library feed query
 */
export const getQuestionLibraryFeedQueryVariables = state => {
  const curriculumProgramId = getSelectedCurriculumId(state);
  const orgId = _.get(state, "login.userInfo.org_id");
  const filters = _.get(state, "questionLibrary.feedFilters", {});

  const variables = {
    orgResourceFilters: {
      types: ["QUESTION_BANK_RESOURCE"],
      resourceType: "QUESTION",
      curriculumProgramId,
      questionFilters: { isArchived: false },
    },
    first: 10,
    orgId,
    fetchConversations: true,
    orderBy: "CREATED_AT",
  };

  const plannerElements = _.get(filters, "plannerElements", []);
  const objectives = _.get(filters, "objectives", []);
  const criterion = _.map(_.get(filters, "criterion", null), "value");
  const keyConcepts = _.map(_.get(filters, "keyConcepts", []), "value");
  const globalContext = _.map(_.get(filters, "globalContext", []), "value");
  const relatedConcepts = _.map(_.get(filters, "relatedConcepts", []), "value");
  const allPlannerNodes = _.compact(
    _.concat(
      objectives,
      criterion,
      keyConcepts,
      globalContext,
      relatedConcepts,
      plannerElements
    )
  );

  const subjects =
    _.isEmpty(objectives) || _.isEmpty(plannerElements)
      ? _.get(filters, "subjects", [])?.value
      : [];

  if (!_.isEmpty(subjects))
    _.set(variables, "orgResourceFilters.questionFilters.subjects", subjects);

  const grades = _.map(_.get(filters, "grades", null), "value");
  if (!_.isEmpty(grades))
    _.set(variables, "orgResourceFilters.questionFilters.grades", grades);

  if (!_.isEmpty(allPlannerNodes))
    _.set(
      variables,
      "orgResourceFilters.questionFilters.plannerElementNodes",
      allPlannerNodes
    );

  const paperTypes = _.map(_.get(filters, "paperTypes", null), "value");
  if (!_.isEmpty(paperTypes))
    _.set(
      variables,
      "orgResourceFilters.questionFilters.paperTypes",
      paperTypes
    );

  const questionTypes = _.map(_.get(filters, "questionTypes", null), "value");
  if (!_.isEmpty(questionTypes))
    _.set(
      variables,
      "orgResourceFilters.questionFilters.questionTypes",
      questionTypes
    );

  const subjectLevelsFilter = _.get(filters, "subjectLevels", []);

  if (!_.isEmpty(subjectLevelsFilter)) {
    const subjectLevels = [
      {
        id: _.get(subjectLevelsFilter, "value", null),
        label: _.get(subjectLevelsFilter, "label", null),
      },
    ];
    _.set(
      variables,
      "orgResourceFilters.questionFilters.subjectLevels",
      subjectLevels
    );
  }

  const createdBy = _.map(_.get(filters, "createdBy", []), "value");
  if (!_.isEmpty(createdBy))
    _.set(variables, "orgResourceFilters.questionFilters.createdBy", createdBy);

  const reviewer = _.map(_.get(filters, "reviewer", []), "value");
  if (!_.isEmpty(reviewer))
    _.set(variables, "orgResourceFilters.questionFilters.reviewedBy", reviewer);

  const difficulty = _.map(_.get(filters, "difficulty", null), "value");
  if (!_.isEmpty(difficulty))
    _.set(
      variables,
      "orgResourceFilters.questionFilters.difficulty",
      difficulty
    );

  const status = _.get(filters, "status", null);
  if (!_.isEmpty(status))
    _.set(variables, "orgResourceFilters.questionFilters.status", status);

  const isArchived = _.get(filters, "isArchived", null);
  if (_.isBoolean(isArchived))
    _.set(
      variables,
      "orgResourceFilters.questionFilters.isArchived",
      isArchived
    );

  const searchText = _.get(filters, "searchText", null);
  if (!_.isEmpty(searchText))
    _.set(
      variables,
      "orgResourceFilters.questionFilters.searchText",
      searchText
    );

  return variables;
};

/**
 * @param {Object} state - redux state
 * @returns {Object} variables for getQuestionLibraryFeedQueryV2
 */
export const getQuestionLibraryFeedQueryVariablesV2 = state => {
  const curriculumProgramId = getSelectedCurriculumId(state);
  const orgId = _.get(state, "login.userInfo.org_id");
  const filters = _.get(state, "questionLibrary.feedFilters", {});

  const variables = {
    questionFilters: {},
    first: 10,
    orgId,
    orderBy: "CREATED_AT",
  };

  const plannerElements = _.get(filters, "plannerElements", []);
  const objectives = _.map(_.get(filters, "objectives", []), "value");
  const criterion = _.map(_.get(filters, "criterion", null), "value");
  const keyConcepts = _.map(_.get(filters, "keyConcepts", []), "value");
  const globalContext = _.map(_.get(filters, "globalContext", []), "value");
  const relatedConcepts = _.map(_.get(filters, "relatedConcepts", []), "value");
  const allPlannerNodes = _.compact(
    _.concat(
      objectives,
      criterion,
      keyConcepts,
      globalContext,
      relatedConcepts,
      plannerElements
    )
  );

  const shouldIncludeSubject =
    _.isEmpty(objectives) || _.isEmpty(plannerElements);
  const subjectId = shouldIncludeSubject
    ? _.get(filters, "subjects", null)?.value
    : null;
  if (subjectId) _.set(variables, "questionFilters.subjects", [subjectId]);

  const grades = _.map(_.get(filters, "grades", null), "value");
  if (!_.isEmpty(grades)) _.set(variables, "questionFilters.grades", grades);
  if (!_.isEmpty(allPlannerNodes))
    _.set(variables, "questionFilters.plannerElementNodes", allPlannerNodes);

  const paperTypes = _.map(_.get(filters, "paperTypes", null), "value");
  if (!_.isEmpty(paperTypes))
    _.set(variables, "questionFilters.paperTypes", paperTypes);

  const questionTypes = _.map(_.get(filters, "questionTypes", null), "value");
  if (!_.isEmpty(questionTypes))
    _.set(variables, "questionFilters.questionTypes", questionTypes);

  const subjectLevelsFilter = _.get(filters, "subjectLevels", []);
  if (!_.isEmpty(subjectLevelsFilter)) {
    _.set(variables, "questionFilters.subjectLevels", [
      {
        id: _.get(subjectLevelsFilter, "value", null),
        label: _.get(subjectLevelsFilter, "label", null),
      },
    ]);
  }

  const createdBy = _.map(_.get(filters, "createdBy", []), "value");
  if (!_.isEmpty(createdBy))
    _.set(variables, "questionFilters.createdBy", createdBy);

  const reviewer = _.map(_.get(filters, "reviewer", []), "value");
  if (!_.isEmpty(reviewer))
    _.set(variables, "questionFilters.reviewedBy", reviewer);

  const difficulty = _.map(_.get(filters, "difficulty", null), "value");
  if (!_.isEmpty(difficulty))
    _.set(variables, "questionFilters.difficulty", difficulty);

  const status = _.get(filters, "status", null);
  if (!_.isEmpty(status)) _.set(variables, "questionFilters.status", status);

  const isArchived = _.get(filters, "isArchived", false);
  _.set(variables, "questionFilters.isArchived", isArchived);

  const searchText = _.get(filters, "searchText", null);
  if (!_.isEmpty(searchText))
    _.set(variables, "questionFilters.searchText", searchText);

  if (!_.isEmpty(curriculumProgramId)) {
    _.set(variables, "questionFilters.curriculumProgramIds", [
      curriculumProgramId,
    ]);
  }

  return variables;
};

/**
 * @deprecated - use getUpdatedQuestionCacheDataV2 instead
 * @param {Object} data - the current question data in cache
 * @param {Object} variables - the variables passed to the update question mutation
 * @returns - the updated question data to be set in cache
 */
export const getUpdatedQuestionCacheData = (data, variables) => {
  const updatedData = _.cloneDeep(data);

  const { associatedEntities, questionData } = _.get(variables, "input", {});

  const updatedQuestionData = _.get(updatedData, "item", {});

  if (_.get(questionData, "difficulty", false)) {
    updatedQuestionData["difficulty"] = questionData.difficulty;
  }
  if (_.get(questionData, "status", false)) {
    updatedQuestionData["status"] = questionData.status;
  }
  if (_.isBoolean(_.get(questionData, "isArchived", null))) {
    updatedQuestionData["isArchived"] = questionData.isArchived;
  }

  updatedQuestionData["updatedAt"] = getTodaysDate();

  let updatedAssociatedEntities = _.get(data, "item.associatedEntities", []);

  if (associatedEntities?.addedAssociatedEntities) {
    const addedEntities = associatedEntities.addedAssociatedEntities;
    const addedEntityData = _.map(addedEntities, entity => {
      return getQuestionAssociatedEntityFromCache(entity.id, entity.type);
    });

    updatedAssociatedEntities = _.concat(
      updatedAssociatedEntities,
      addedEntityData
    );
  }

  if (associatedEntities?.removedAssociatedEntities) {
    const removedEntityIds = _.map(
      associatedEntities.removedAssociatedEntities,
      "id"
    );

    updatedAssociatedEntities = _.filter(updatedAssociatedEntities, entity => {
      return !_.includes(removedEntityIds, entity.id);
    });
  }

  updatedQuestionData["associatedEntities"] = updatedAssociatedEntities;
  updatedData["item"] = updatedQuestionData;

  return {
    node: updatedData,
  };
};

/**
 * V2 version of getUpdatedQuestionCacheData
 * In V2, the Question is directly the cache node (no .item wrapper)
 */
export const getUpdatedQuestionCacheDataV2 = (data, variables) => {
  const updatedData = _.cloneDeep(data);

  const { associatedEntities, questionData } = _.get(variables, "input", {});

  if (_.get(questionData, "difficulty", false)) {
    updatedData["difficulty"] = questionData.difficulty;
  }
  if (_.get(questionData, "status", false)) {
    updatedData["status"] = questionData.status;
  }
  if (_.isBoolean(_.get(questionData, "isArchived", null))) {
    updatedData["isArchived"] = questionData.isArchived;
  }

  updatedData["updatedAt"] = getTodaysDate();

  let updatedAssociatedEntities = _.get(data, "associatedEntities", []);

  if (associatedEntities?.addedAssociatedEntities) {
    const addedEntities = associatedEntities.addedAssociatedEntities;
    const addedEntityData = _.map(addedEntities, entity => {
      return getQuestionAssociatedEntityFromCache(entity.id, entity.type);
    });

    updatedAssociatedEntities = _.concat(
      updatedAssociatedEntities,
      addedEntityData
    );
  }

  if (associatedEntities?.removedAssociatedEntities) {
    const removedEntityIds = _.map(
      associatedEntities.removedAssociatedEntities,
      "id"
    );

    updatedAssociatedEntities = _.filter(updatedAssociatedEntities, entity => {
      return !_.includes(removedEntityIds, entity.id);
    });
  }

  updatedData["associatedEntities"] = updatedAssociatedEntities;

  return {
    node: updatedData,
  };
};

/**
 * @description gets the number of filters that are selected from the filters in the
 *              add filters modal from the filters object
 * @param {Object} filters
 * @returns count of selected filters
 */
const _getSelectedFeedFiltersCount = ({ feedFilters }) => {
  let count = 0;

  /** objectives and plannerElements are not filters that are directly shown in ui rather they are a combination topics, subtopics, contents so we don't count them */
  const updatedFiltersModalKeys = _.without(
    questionFeedFilterModalKeys,
    "objectives",
    "plannerElements"
  );
  _.forEach(feedFilters, (filter, key) => {
    if (_.includes(updatedFiltersModalKeys, key) && !_.isEmpty(filter)) {
      count++;
    }
  });

  return count;
};

export const getSelectedFeedFiltersCount = _.memoize(params =>
  _getSelectedFeedFiltersCount(params)
);

/**
 * @param {Object} updatedQuestionData
 * @param {Object} questionLibraryFeedCacheData
 * @param {Number} filteredQuestionCount
 * @param {String} feedType
 * @returns the updated question library feed data if status or isArchived is updated
 */
export const getUpdatedQuestionLibraryFeedData = ({
  updatedQuestionData,
  questionLibraryFeedCacheData,
  filteredQuestionCount,
  feedType,
}) => {
  const updatedStatus = _.get(updatedQuestionData, "node.item.status", null);
  const questionId = _.get(updatedQuestionData, "node.item.id", null);
  const updatedIsArchived = _.get(
    updatedQuestionData,
    "node.item.isArchived",
    false
  );

  const isArchived = _.get(
    _.find(
      _.get(questionLibraryFeedCacheData, "node.organizationResourcesV2.edges"),
      ["node.item.id", questionId]
    ),
    "node.item.isArchived",
    false
  );

  let updatedQuestionLibraryFeedData = questionLibraryFeedCacheData;

  const isUpdateQuestionFeedCatchData =
    (feedType !== ALL_QUESTION &&
      feedType !== ARCHIVED &&
      updatedStatus !== feedType) ||
    updatedIsArchived !== isArchived;

  if (
    isUpdateQuestionFeedCatchData &&
    !_.isEmpty(questionLibraryFeedCacheData)
  ) {
    const updatedQuestionFeed = _.filter(
      _.get(
        questionLibraryFeedCacheData,
        "node.organizationResourcesV2.edges",
        []
      ),
      ({ node }) => _.get(node, "item.id") !== questionId
    );
    updatedQuestionLibraryFeedData = update(questionLibraryFeedCacheData, {
      node: {
        organizationResourcesV2: {
          resourceTypeBasedCount: {
            totalCount: { $set: filteredQuestionCount - 1 },
          },
          edges: {
            $set: updatedQuestionFeed,
          },
        },
      },
    });
  }
  return { updatedQuestionLibraryFeedData, isUpdateQuestionFeedCatchData };
};

/**
 * @description - gets the updated question library feed data for getQuestionLibraryFeedQueryV2
 * @returns updated feed data for getQuestionLibraryFeedQueryV2
 */
export const getUpdatedQuestionLibraryFeedDataV2 = ({
  updatedQuestionData,
  questionLibraryFeedCacheData,
  filteredQuestionCount,
  feedType,
}) => {
  const updatedStatus = _.get(updatedQuestionData, "node.status", null);
  const questionId = _.get(updatedQuestionData, "node.id", null);
  const updatedIsArchived = _.get(
    updatedQuestionData,
    "node.isArchived",
    false
  );

  const existingQuestion = _.find(
    _.get(questionLibraryFeedCacheData, "node.questions.edges", []),
    ["id", questionId]
  );
  const isArchived = _.get(existingQuestion, "isArchived", false);

  let updatedQuestionLibraryFeedData = questionLibraryFeedCacheData;

  const isUpdateQuestionFeedCatchData =
    (feedType !== ALL_QUESTION &&
      feedType !== ARCHIVED &&
      updatedStatus !== feedType) ||
    updatedIsArchived !== isArchived;

  if (
    isUpdateQuestionFeedCatchData &&
    !_.isEmpty(questionLibraryFeedCacheData)
  ) {
    const updatedEdges = _.filter(
      _.get(questionLibraryFeedCacheData, "node.questions.edges", []),
      edge => _.get(edge, "id") !== questionId
    );

    updatedQuestionLibraryFeedData = update(questionLibraryFeedCacheData, {
      node: {
        questions: {
          count: {
            totalCount: { $set: filteredQuestionCount - 1 },
          },
          edges: { $set: updatedEdges },
        },
      },
    });
  }

  return { updatedQuestionLibraryFeedData, isUpdateQuestionFeedCatchData };
};

/**
 * @param {nodes}           Object - list of syllabus content sets of all subjects
 * @param {selectedSubject} Object - selected subject option
 * @returns                        - list of all nodes as id based key value pair for quick reference and the
 *                                    topic, subtopic, content dropdown options of the selected subjects
 */
const _getSyllabusContentQuestionFeedFilters = ({
  nodes,
  selectedSubject,
  isMYP,
  relatedConcepts,
  selectedGrades,
}) => {
  const topicOptions = [],
    subtopicOptions = [],
    contentOptions = [],
    relatedConceptOptions = [];

  const addOptionsBasedOnDepth = node => {
    const option = {
      value: isMYP ? _.get(node, "id", "") : node,
      label: _.get(node, "label", ""),
    };

    switch (_.get(node, "depth", 0)) {
      case TOPIC_DEPTH:
        topicOptions.push(option);
        break;
      case SUB_TOPIC_DEPTH:
        subtopicOptions.push(option);
        break;
      case CONTENT_DEPTH:
        contentOptions.push(option);
        break;
      default:
        break;
    }
  };

  if (isMYP) {
    // For MYP Objectives and related concepts
    const subjectId = _.get(selectedSubject, "value", null);
    const gradeId = _.first(_.map(selectedGrades, "value", []));

    const parentNodes = _.filter(
      _.get(nodes, "plannerElementSets[0].nodes", []),
      ({ associatedParents }) =>
        _.some(associatedParents, parent => parent.id === subjectId) &&
        _.some(associatedParents, parent => parent.id === gradeId)
    );

    //MYP objectives are associated with both subject and grade
    if (!_.isEmpty(subjectId) && !_.isEmpty(gradeId)) {
      _.forEach(parentNodes, addOptionsBasedOnDepth);
    }
    //Related concepts are associated only with subject
    if (!_.isEmpty(subjectId)) {
      _.forEach(relatedConcepts, concept => {
        const associatedParents = _.get(concept, "associatedParents", []);
        if (_.some(associatedParents, { id: subjectId })) {
          relatedConceptOptions.push({
            value: concept?.id,
            label: concept?.label,
          });
        }
      });
    }
  } else {
    if (!_.isEmpty(selectedSubject)) {
      const subjectData = _.find(nodes, { id: selectedSubject.value });
      const associatedChildPlannerElementNodes = _.get(
        subjectData,
        "associatedChildPlannerElementNodes",
        []
      );
      _.forEach(associatedChildPlannerElementNodes, addOptionsBasedOnDepth);
    }
  }

  return {
    topicOptions,
    subtopicOptions,
    contentOptions,
    relatedConceptOptions,
  };
};

export const getSyllabusContentQuestionFeedFilters = _.memoize(
  params => _getSyllabusContentQuestionFeedFilters(params),
  ({ selectedSubject, nodes, isMYP, relatedConcepts, selectedGrades }) => {
    let nodesData = [];
    const subjectId = _.get(selectedSubject, "value", "");
    const gradeIds = _.join(_.map(selectedGrades, "value"), "|");
    if (isMYP) {
      const mypNodes = _.get(nodes, "plannerElementSets[0].nodes", []);
      nodesData = _.map(mypNodes, node => {
        const associatedParentsIds = _.map(
          _.get(node, "associatedParents", []),
          "id"
        );
        return {
          ..._.pick(node, ["id", "label", "depth"]),
          associatedParents: _.sortBy(associatedParentsIds).join("|"),
        };
      });
    } else {
      const subjectNode = _.find(nodes, { id: subjectId });
      const children = _.get(
        subjectNode,
        "associatedChildPlannerElementNodes",
        []
      );
      nodesData = _.map(children, node =>
        _.pick(node, ["id", "label", "depth"])
      );
    }

    const relatedConceptsData = _.map(relatedConcepts, concept => {
      const associatedParentsIds = _.map(
        _.get(concept, "associatedParents", []),
        "id"
      );
      return {
        ..._.pick(concept, ["id", "label"]),
        associatedParents: _.sortBy(associatedParentsIds).join("|"),
      };
    });

    return JSON.stringify({
      selectedSubject: subjectId,
      selectedGrades: gradeIds,
      nodes: nodesData,
      isMYP,
      relatedConcepts: relatedConceptsData,
    });
  }
);

export const getLibraryTagDetails = (questionResource, t, isSideBarPreview) => {
  const question = _.get(questionResource, "item");
  const groupedTags = _.groupBy(
    _.get(question, "associatedEntities", []),
    "__typename"
  );

  const tagLabels = [];

  const grades = _.get(groupedTags, "Grade", []);
  const gradeLabel = _.get(_.first(grades), "name", "");
  const gradeTooltip = _.map(grades, "name");

  if (!_.isEmpty(gradeLabel)) {
    tagLabels.push(
      <QuestionCardTag
        label={gradeLabel}
        tooltips={gradeTooltip}
        count={_.size(grades) - 1}
        isSideBarPreview={isSideBarPreview}
        t={t}
      />
    );
  }

  const paperTypes = _.get(groupedTags, "GenericTag", []);
  const paperTypeLabel = _.get(_.first(paperTypes), "label", "");
  const paperTypeTooltip = _.map(paperTypes, "label");

  if (!_.isEmpty(paperTypeLabel)) {
    tagLabels.push(
      <QuestionCardTag
        label={paperTypeLabel}
        tooltips={paperTypeTooltip}
        count={_.size(paperTypes) - 1}
        isSideBarPreview={isSideBarPreview}
        t={t}
      />
    );
  }

  return tagLabels;
};

/**
 * Generates dropdown options for question types, excluding those that are question creation methods.
 *
 * @param {Object} params - The parameters object.
 * @param {Function} params.t - Translation function for localizing option labels.
 * @returns {Array<Object>} Array of dropdown option objects with the 'icon' property omitted and 'value' set to the option's 'key'.
 */
export const getDropDownOptions = ({ t }) => {
  return _.map(
    _.filter(
      getFilteredQuestionTypeOptions(QUESTION_TYPES, false, t),
      option => !_.get(option, "isQuestionCreationMethod", false)
    ),
    option => _.omit({ ...option, value: _.get(option, "key") }, "icon")
  );
};

export const getUsers = _.memoize(
  staff => {
    return _.map(_.get(staff, "edges", []), "node");
  },
  staff => {
    const edges = _.get(staff, "edges", []);
    return _.join(_.map(edges, "node.id"), "|");
  }
);

/**
 * Generates dummy submission data for library view from question data.
 *
 * Takes a question data object and extracts all questions including sub-questions,
 * then creates dummy submissions for them.
 *
 * @param {Object} questionData - The question data object containing the main question and child questions
 * @returns {Object} Dummy submissions generated from the questions
 */
export const getDummySubmissionForLibrary = questionData => {
  const questionArray = [];
  const question = _.get(questionData, "item", {});
  questionArray.push(question);

  _.get(questionData, "childrenV2.edges", []).forEach(subQuestionEdge => {
    const subQuestion = _.get(subQuestionEdge, "node.item", {});
    questionArray.push(subQuestion);
  });
  return getDummySubmissionsFromQuestions(questionArray);
};

/**
 * V2 version of getDummySubmissionForLibrary
 */
export const getDummySubmissionForLibraryV2 = questionData => {
  const questionArray = [questionData];
  _.get(questionData, "children.edges", []).forEach(subQuestion => {
    questionArray.push(subQuestion);
  });
  return getDummySubmissionsFromQuestions(questionArray);
};

/**
 * Reorders sub-question edges in the question details cache.
 * Uses the same pattern as worksheet reordering - physically moves items in the array.
 * The server maintains the actual sequence via DISPLAY_SEQUENCE ordering.
 *
 * @deprecated - use getReorderedSubQuestionsCacheDataV2 instead, which is updated for V2 question details cache structure
 * @param {Object} cacheData - The question details cache data
 * @param {number} fromIndex - The index to move from
 * @param {number} toIndex - The index to move to
 * @returns {Object} Updated cache data with reordered sub-questions
 */
export const getReorderedSubQuestionsCacheData = ({
  cacheData,
  fromIndex,
  toIndex,
}) => {
  const subQuestions = _.get(cacheData, "childrenV2.edges", []);
  const updatedSubQuestions = moveElementInArray({
    array: subQuestions,
    fromIndex,
    toIndex,
  });

  const updatedCacheData = update(cacheData, {
    childrenV2: {
      edges: {
        $set: updatedSubQuestions,
      },
    },
  });

  return updatedCacheData;
};

export const getQuestionFeedCardDataV2 = (data, orderBy, statusOptions, t) => {
  const questionNode = _.get(data, "node", {});
  const questionResourceId =
    _.get(questionNode, "parentEntity.id") || _.get(questionNode, "id");
  const questionId = _.get(questionNode, "id", null);
  const createdBy = _.get(questionNode, "createdBy", {});
  const profileImage = _.get(createdBy, "profileImage", "");
  const status = _.get(questionNode, "status", "");
  const isArchived = _.get(questionNode, "isArchived", false);
  const libraryUid = _.get(questionNode, "libraryUid", "");
  const unreadMessageCount = _.get(
    questionNode,
    "conversation.unreadMessageCount",
    0
  );

  const {
    marks,
    title: titleHtmlString,
    questionTypeLabel: type,
  } = getLibraryQuestionCardDetailsV2({ question: questionNode, t });

  const title = titleHtmlString
    ? extractContentFromHTML({ htmlString: titleHtmlString })
    : "";

  const showImageIcon = titleHtmlString
    ? checkForImageInHtmlString(titleHtmlString)
    : false;

  const statusLabel = _.get(
    _.find(statusOptions, { value: status }),
    "label",
    ""
  );
  const statusColor = _.get(QUESTION_STATUS_COLORS, status, "");
  const authorName = getFormattedStaffName({
    staff: { node: createdBy },
    orderBy,
  });

  return {
    questionResourceId,
    unreadMessageCount,
    profileImage,
    questionId,
    isArchived,
    marks,
    title,
    statusLabel,
    statusColor,
    authorName,
    type,
    showImageIcon,
    libraryUid,
  };
};

/**
 * V2 version of getReorderedSubQuestionsCacheData.
 * In V2, sub-questions are at children.edges (not childrenV2.edges).
 */
export const getReorderedSubQuestionsCacheDataV2 = ({
  cacheData,
  fromIndex,
  toIndex,
}) => {
  const subQuestions = _.get(cacheData, "children.edges", []);
  const movedSubQuestions = moveElementInArray({
    array: subQuestions,
    fromIndex,
    toIndex,
  });

  const updatedSubQuestions = _.map(movedSubQuestions, (edge, index) => ({
    ...edge,
    sequence: index + 1,
  }));

  return update(cacheData, {
    children: {
      edges: {
        $set: updatedSubQuestions,
      },
    },
  });
};

/**
 * V2 version — wraps V2 question data (no .item wrapper) into V1 shape
 * and delegates to getAssociatedEntityVariables.
 */
export const getAssociatedEntityVariablesV2 = (
  questionData,
  activeQuestionFilters
) => {
  return getAssociatedEntityVariables(
    { item: questionData },
    activeQuestionFilters
  );
};
